import * as jsvat from '@accountable/jsvat';
import { checkVAT } from '@accountable/jsvat';
import Joi from 'joi';
import { AccountSettingsFieldsNamesEnum } from 'types/settings.types';
import { AvailableCountries } from 'types/users.types';
import { settingsErrorMessagesFormatter } from 'utils/settings/settingsHelpers';
import { groupValidation } from 'validation/commonValidators';

// ===========================
// SPLIT FORM SCHEMAS (per-step)
// ===========================

// Unicode-aware name regex: allows letters from any language, hyphens, apostrophes and spaces as separators
const swanNameRegex = /^[\p{L}]+(?:[-'\s][\p{L}]+)*'?$/u;

const PersonalInfoSchema = () =>
  Joi.object({
    [AccountSettingsFieldsNamesEnum.firstName]: Joi.string()
      .trim()
      .required()
      .max(100)
      .pattern(swanNameRegex)
      .messages(
        settingsErrorMessagesFormatter(
          AccountSettingsFieldsNamesEnum.firstName,
        ),
      ),
    [AccountSettingsFieldsNamesEnum.lastName]: Joi.string()
      .trim()
      .required()
      .max(100)
      .pattern(swanNameRegex)
      .messages(
        settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.lastName),
      ),
    [AccountSettingsFieldsNamesEnum.email]: Joi.string()
      .required()
      .email({ tlds: false }),
  });

const BEBusinessAddressSchema = () =>
  Joi.object({
    ...groupValidation(AccountSettingsFieldsNamesEnum.address, {
      [AccountSettingsFieldsNamesEnum.street]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.street),
        ),
      [AccountSettingsFieldsNamesEnum.city]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.city),
        ),
      [AccountSettingsFieldsNamesEnum.zip]: Joi.string()
        .required()
        .length(4)
        .messages(
          settingsErrorMessagesFormatter(
            `${AccountSettingsFieldsNamesEnum.zip}.be`,
          ),
        ),
    }),
  });

const DEBusinessAddressSchema = () =>
  Joi.object({
    ...groupValidation(AccountSettingsFieldsNamesEnum.address, {
      [AccountSettingsFieldsNamesEnum.street]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.street),
        ),
      [AccountSettingsFieldsNamesEnum.city]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.city),
        ),
      [AccountSettingsFieldsNamesEnum.zip]: Joi.string()
        .required()
        .length(5)
        .messages(
          settingsErrorMessagesFormatter(
            `${AccountSettingsFieldsNamesEnum.zip}.de`,
          ),
        ),
      [AccountSettingsFieldsNamesEnum.state]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.state),
        ),
    }),
  });

const BusinessActivitySchema = () =>
  Joi.object({
    [AccountSettingsFieldsNamesEnum.APC1]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.APC2]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.monthlyPaymentVolume]:
      Joi.string().required(),
  });

const BEBusinessActivitySchema = () =>
  Joi.object({
    [AccountSettingsFieldsNamesEnum.VATNumber]: Joi.string()
      .required()
      .custom((value, helpers) => {
        const { isValid } = checkVAT(value, [jsvat.belgium]);

        if (!isValid) {
          return helpers.error('any.invalid');
        }
        return value;
      })
      .messages(
        settingsErrorMessagesFormatter(
          AccountSettingsFieldsNamesEnum.VATNumber,
        ),
      ),
    [AccountSettingsFieldsNamesEnum.APC1]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.APC2]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.monthlyPaymentVolume]:
      Joi.string().required(),
  });

const DEBusinessActivitySchema = () =>
  Joi.object({
    [AccountSettingsFieldsNamesEnum.steuernummer]: Joi.when(
      AccountSettingsFieldsNamesEnum.state,
      {
        is: Joi.string().exist(),
        then: Joi.required().when(Joi.ref('/country'), {
          is: AvailableCountries.germany,
          then: Joi.custom((value, helpers) => {
            const values = helpers.state.ancestors[0];

            if (values.steuernummerValid === false) {
              return helpers.error('any.invalid');
            }
            return value;
          }).messages(
            settingsErrorMessagesFormatter(
              AccountSettingsFieldsNamesEnum.steuernummer,
            ),
          ),
        }),
      },
    ),
    ...groupValidation(AccountSettingsFieldsNamesEnum.address, {
      [AccountSettingsFieldsNamesEnum.state]: Joi.any(),
    }),
    [AccountSettingsFieldsNamesEnum.APC1]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.APC2]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.monthlyPaymentVolume]:
      Joi.string().required(),
  });

const BornDetailsSchema = () =>
  Joi.object({
    birthCity: Joi.string().required().max(100),
    birthPostalCode: Joi.string().required().max(50),
    birthCountry: Joi.string().required().length(3),
    nationality: Joi.string().required().length(3),
  });

const ResidencyDetailsSchema = () =>
  Joi.object({
    residencyCountry: Joi.string()
      .required()
      .length(3)
      .pattern(/^[A-Za-z]{3}$/),
    residencyStreet: Joi.string().required().max(255),
    residencyCity: Joi.string().required().max(100),
    residencyZip: Joi.string().required().max(12),
  });

// ===========================
// LEGACY FULL FORM SCHEMAS (kept for backwards compatibility)
// ===========================

const BEOnboardingFormSchema = () =>
  Joi.object({
    [AccountSettingsFieldsNamesEnum.VATNumber]: Joi.string()
      .required()
      .custom((value, helpers) => {
        const { isValid } = checkVAT(value, [jsvat.belgium]);

        if (!isValid) {
          return helpers.error('any.invalid');
        }
        return value;
      })
      .messages(
        settingsErrorMessagesFormatter(
          AccountSettingsFieldsNamesEnum.VATNumber,
        ),
      ),
    [AccountSettingsFieldsNamesEnum.APC1]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.APC2]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.monthlyPaymentVolume]:
      Joi.string().required(),
    ...groupValidation(AccountSettingsFieldsNamesEnum.address, {
      [AccountSettingsFieldsNamesEnum.street]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.street),
        ),
      [AccountSettingsFieldsNamesEnum.city]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.city),
        ),
      [AccountSettingsFieldsNamesEnum.zip]: Joi.string()
        .required()
        .length(4)
        .messages(
          settingsErrorMessagesFormatter(
            `${AccountSettingsFieldsNamesEnum.zip}.be`,
          ),
        ),
    }),
  });

const DEOnboardingFormSchema = () =>
  Joi.object({
    [AccountSettingsFieldsNamesEnum.steuernummer]: Joi.when(
      AccountSettingsFieldsNamesEnum.state,
      {
        is: Joi.string().exist(),
        then: Joi.required().when(Joi.ref('/country'), {
          is: AvailableCountries.germany,
          then: Joi.custom((value, helpers) => {
            const values = helpers.state.ancestors[0];

            if (values.steuernummerValid === false) {
              return helpers.error('any.invalid');
            }
            return value;
          }).messages(
            settingsErrorMessagesFormatter(
              AccountSettingsFieldsNamesEnum.steuernummer,
            ),
          ),
        }),
      },
    ),
    [AccountSettingsFieldsNamesEnum.APC1]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.APC2]: Joi.string().required(),
    [AccountSettingsFieldsNamesEnum.monthlyPaymentVolume]:
      Joi.string().required(),
    ...groupValidation(AccountSettingsFieldsNamesEnum.address, {
      [AccountSettingsFieldsNamesEnum.street]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.street),
        ),
      [AccountSettingsFieldsNamesEnum.city]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.city),
        ),
      [AccountSettingsFieldsNamesEnum.zip]: Joi.string()
        .required()
        .length(5)
        .messages(
          settingsErrorMessagesFormatter(
            `${AccountSettingsFieldsNamesEnum.zip}.de`,
          ),
        ),
      [AccountSettingsFieldsNamesEnum.state]: Joi.string()
        .required()
        .messages(
          settingsErrorMessagesFormatter(AccountSettingsFieldsNamesEnum.state),
        ),
    }),
  });

export {
  DEOnboardingFormSchema,
  BEOnboardingFormSchema,
  PersonalInfoSchema,
  BEBusinessAddressSchema,
  DEBusinessAddressSchema,
  BusinessActivitySchema,
  BEBusinessActivitySchema,
  DEBusinessActivitySchema,
  BornDetailsSchema,
  ResidencyDetailsSchema,
};
